const CollectionHelpers = {
    id: (idOrCollection?: unknown) => {
        if (!idOrCollection) {
            return '';
        }

        if (typeof idOrCollection === 'string') {
            return idOrCollection;
        }

        if (typeof idOrCollection === 'object') {
            if (isObjectId(idOrCollection)) {
                return String(idOrCollection);
            }

            if ('id' in idOrCollection) {
                return String(idOrCollection.id);
            }

            if ('_id' in idOrCollection) {
                return String(idOrCollection._id);
            }
        }

        return '';
    },

    ids: <T>(array?: T[] | null, key?: keyof T) => {
        return (array || []).map((item) => CollectionHelpers.id(key ? item[key] : item));
    },

    /**
     * Payload disregards an invalid `id` and matches nothing, but only for values it
     * reads as single: a comma sends it down a list branch that has no such guard and
     * throws. Filter on `id` only when the value could actually be one.
     */
    isObjectIdString: (value: string) => /^[0-9a-f]{24}$/i.test(value),

    equals: (a: unknown, b: unknown) => {
        const aa = CollectionHelpers.id(a);
        const bb = CollectionHelpers.id(b);

        return !!aa && !!bb && aa === bb;
    },

    find: <T extends {}>(array: T[] | null = [], value?: CollectionOrId | number | null, key?: keyof T) => {
        if (!value) {
            return null;
        }

        return (
            (array || []).find((needle) => {
                return CollectionHelpers.equals(key ? needle[key] : needle, value);
            }) || null
        );
    },

    contains: <T extends {}>(array: T[] | null = [], value?: CollectionOrId | number | null, key?: keyof T) => {
        if (!value) {
            return false;
        }

        return (array || []).some((needle) => {
            return CollectionHelpers.equals(key ? needle[key] : needle, value);
        });
    },

    filter: <T extends {}>(array: T[] | null = [], value?: CollectionOrId | number | null, key?: keyof T) => {
        if (!value) {
            return [];
        }

        return (array || []).filter((needle) => {
            return CollectionHelpers.equals(key ? needle[key] : needle, value);
        });
    },

    exclude: <T extends {}>(array: T[] | null = [], value?: CollectionOrId | number | null, key?: keyof T) => {
        if (!value) {
            return array || [];
        }

        return (array || []).filter((needle) => {
            return !CollectionHelpers.equals(key ? needle[key] : needle, value);
        });
    },
};

// Duck-typed instead of `instanceof Types.ObjectId`: importing mongoose here puts its
// 570 kB browser build in the shared client bundle, since this helper is used everywhere.
const isObjectId = (value: object) =>
    '_bsontype' in value && (value._bsontype === 'ObjectId' || value._bsontype === 'ObjectID');

export { CollectionHelpers };
